import { useCallback } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import type { NotificationDocument } from '@/lib/db/rxdb/schemas/notification'
import { useNotificationMutations } from '@/lib/db/rxdb/hooks'
import { logger } from '@/lib/monitoring/logger'
import useLoginStore from '@/store/LoginStore'

/**
 * Actions for the notifications inbox. Everything goes through the RxDB
 * collection so it works offline and syncs like any other change. The backend
 * push path only accepts IsRead/ReadAt and soft-delete state from clients,
 * which is exactly what these touch.
 */
export function useNotificationActions() {
  const { t } = useTranslation('notifications')
  const { updateNotification } = useNotificationMutations()
  const userId = useLoginStore((s) => s.userId)
  const navigate = useNavigate()

  const markAsRead = useCallback(
    async (notificationId: string) => {
      await updateNotification(notificationId, { isRead: true, readAt: Date.now() })
    },
    [updateNotification],
  )

  /** Marks each unread notification in `notifications` as read. */
  const markManyAsRead = useCallback(
    async (notifications: ReadonlyArray<NotificationDocument>) => {
      const unread = notifications.filter((n) => !n.isRead)
      const readAt = Date.now()
      await Promise.all(unread.map((n) => updateNotification(n.id, { isRead: true, readAt })))
    },
    [updateNotification],
  )

  /**
   * "Mark all read". Done locally through RxDB rather than the REST
   * `markAllAsRead` endpoint: a server-side bulk update followed by a stale
   * local push would conflict, and the local path is instant and offline-safe.
   */
  const markAllAsRead = useCallback(
    async (notifications: ReadonlyArray<NotificationDocument>) => {
      try {
        await markManyAsRead(notifications)
      } catch (error) {
        logger.logError(error as Error, 'DATABASE', { operation: 'notifications_mark_all_read' })
        toast.error(t('toast.failed'))
      }
    },
    [markManyAsRead, t],
  )

  const restore = useCallback(
    async (ids: ReadonlyArray<string>) => {
      await Promise.all(
        ids.map((id) => updateNotification(id, { isDeleted: false, deletedOn: null, deletedBy: null })),
      )
    },
    [updateNotification],
  )

  /**
   * Done = dismiss: soft-deletes the notifications (a stack dismisses all of
   * its updates) and offers Undo, which restores them.
   */
  const markDone = useCallback(
    async (notifications: ReadonlyArray<NotificationDocument>) => {
      const ids = notifications.map((n) => n.id)
      if (ids.length === 0) return
      try {
        // A soft delete through `update` (not `delete`) so it retries on an
        // RxDB revision conflict, e.g. when the row was just marked read.
        const deletedOn = Date.now()
        await Promise.all(
          ids.map((id) =>
            updateNotification(id, { isDeleted: true, deletedOn, deletedBy: userId }),
          ),
        )
      } catch (error) {
        logger.logError(error as Error, 'DATABASE', { operation: 'notifications_mark_done' })
        toast.error(t('toast.failed'))
        return
      }
      toast(t('toast.done', { count: ids.length }), {
        action: {
          label: t('toast.undo'),
          onClick: () => {
            restore(ids).catch((error: unknown) => {
              logger.logError(error as Error, 'DATABASE', { operation: 'notifications_undo_done' })
              toast.error(t('toast.failed'))
            })
          },
        },
      })
    },
    [restore, t, updateNotification, userId],
  )

  const handleNotificationClick = useCallback(
    async (notification: NotificationDocument) => {
      if (!notification.isRead) await markAsRead(notification.id)
      if (notification.actionUrl) navigate({ to: notification.actionUrl })
    },
    [markAsRead, navigate],
  )

  return {
    markAsRead,
    markManyAsRead,
    markAllAsRead,
    markDone,
    restore,
    handleNotificationClick,
  }
}
